import { getLibraryFolders, readScopesEnabled } from "@/api";
import { CreateScopeDialog } from "@/components/frontend/dialogs/create-scope/CreateScopeDialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { UploadDialog } from "@/pages/frontend/documents/components/UploadDialog";
import { useQuery } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import {
    ChevronDown,
    FileSearch2,
    MessageSquare,
    Plus,
    Upload,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

/**
 * Topbar "+ Create" menu: the global entry point for starting a scope,
 * a document upload, or a conversation from any page.
 */
export function CreateMenu() {
    const { organization, settings } = useOrganization();
    const dialogs = useDialogs();
    const navigate = useNavigate();
    const [uploadOpen, setUploadOpen] = useState(false);

    // The topbar renders on every page; defer the folders fetch until the dialog opens.
    const { data: foldersData } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
        enabled: uploadOpen,
    });

    const handleCreateScope = () => {
        dialogs.open(CreateScopeDialog, {
            organizationId: organization.id,
            organizationIndustryId: organization.industry_id,
        });
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        // The label span is display:none on mobile, which drops
                        // it from the accessible name; the aria-label keeps the
                        // icon-only trigger named.
                        <Button aria-label="Create">
                            <Plus className="size-4" />
                            <span className="hidden sm:inline">Create</span>
                            <ChevronDown className="hidden size-4 sm:block" />
                        </Button>
                    }
                />
                <DropdownMenuContent className="w-40" align="start">
                    {readScopesEnabled(settings) && (
                        <DropdownMenuItem onClick={handleCreateScope}>
                            <FileSearch2 />
                            New Scope
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setUploadOpen(true)}>
                        <Upload />
                        New Document
                    </DropdownMenuItem>
                    {settings.ai_chat_enabled && (
                        <DropdownMenuItem
                            onClick={() => navigate("/organizations/chat")}
                        >
                            <MessageSquare />
                            New Conversation
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                folderId={null}
                isKnowledgeBase={false}
                allowSpreadsheets
                folders={foldersData?.folders ?? []}
                // UploadDialog invalidates the library queries itself.
                onUploadComplete={() => {}}
            />
        </>
    );
}
